import { DateRangeObject, TablePaginatedOptions } from '@acceso/design-system';

import { fromAppWallClockEdge, toAppWallClock } from '@/utils/spain-time';

type TRangeEnd = Date | null | undefined;

type TAppTzRangeEdge = {
  /** What to hand the design-system picker: the Madrid wall clock of each end. */
  passed: DateRangeObject | undefined;
  /** Turns what the picker emits back into instants. */
  fromEmitted: (emitted: DateRangeObject | null | undefined) => DateRangeObject;
};

function toPickerEnd(end: TRangeEnd): Date | null {
  return end ? toAppWallClock(end) : null;
}

function fromPickerEnd(emitted: TRangeEnd, passed: TRangeEnd, original: TRangeEnd): Date | null {
  return emitted ? fromAppWallClockEdge(emitted, passed, original) : null;
}

/**
 * INPUT EDGE for design-system range pickers, which only work in the browser's
 * clock (see `@/utils/spain-time`). Each end is shown as its Madrid wall clock
 * and read back as the instant it names; an end handed back untouched keeps
 * its original instant.
 */
export function createAppTzRangeEdge(
  original: DateRangeObject | null | undefined
): TAppTzRangeEdge {
  const passed = original
    ? { start: toPickerEnd(original.start), end: toPickerEnd(original.end) }
    : undefined;

  return {
    passed,
    fromEmitted: emitted => ({
      start: fromPickerEnd(emitted?.start, passed?.start, original?.start),
      end: fromPickerEnd(emitted?.end, passed?.end, original?.end),
    }),
  };
}

type TTableDateFilter = NonNullable<TablePaginatedOptions['dateFilter']>;

/**
 * One edge per `initialValue` object. A table that memoises its initial range —
 * a fresh object on every render makes the picker discard a selection the user
 * has not applied yet — keeps handing the picker the same object.
 */
const tableEdges = new WeakMap<DateRangeObject, TAppTzRangeEdge>();

function getTableEdge(initialValue: DateRangeObject | undefined): TAppTzRangeEdge {
  if (!initialValue) return createAppTzRangeEdge(initialValue);

  const cached = tableEdges.get(initialValue);
  if (cached) return cached;

  const edge = createAppTzRangeEdge(initialValue);
  tableEdges.set(initialValue, edge);

  return edge;
}

/**
 * Wraps a design-system table `dateFilter` so its picker shows and reads Madrid
 * wall clock, whatever the browser's timezone. `onChange` receives instants.
 */
export function withAppTzDateFilter(dateFilter: TTableDateFilter): TTableDateFilter {
  const edge = getTableEdge(dateFilter.initialValue);

  return {
    ...dateFilter,
    initialValue: edge.passed,
    onChange: (value: DateRangeObject) => {
      // A cleared filter reaches the callers as it did before: they tell it apart.
      if (!value) return dateFilter.onChange(value);

      dateFilter.onChange(edge.fromEmitted(value));
    },
  };
}
